import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import type { AccountReport, PostMetrics } from './types';

dayjs.extend(isoWeek);

export function formatCompact(n?: number | null): string {
    if (n == null || Number.isNaN(n)) return '—';
    const abs = Math.abs(n);
    if (abs < 1000) return String(Math.round(n));
    const units = ['K', 'M', 'B'];
    const order = Math.min(Math.floor(Math.log10(abs) / 3), units.length);
    const scaled = n / Math.pow(1000, order);
    const rounded = Math.round(scaled * 10) / 10;
    const body = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
    return `${body}${units[order - 1]}`;
}

export function formatPct(n?: number | null): string {
    if (n == null || Number.isNaN(n)) return '—';
    return `${(n * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

export function postsOf(report: AccountReport): PostMetrics[] {
    return report.posts ?? [];
}

export function postMoment(post: PostMetrics): dayjs.Dayjs | null {
    const raw = post.has_time && post.parsed_time && !post.parsed_time.startsWith('0001')
        ? post.parsed_time
        : post.create_time;
    if (!raw) return null;
    const d = dayjs(raw);
    return d.isValid() ? d : null;
}

export function summarise(posts: PostMetrics[]) {
    return posts.reduce(
        (acc, p) => ({
            views: acc.views + (p.views || 0),
            likes: acc.likes + (p.likes || 0),
            comments: acc.comments + (p.comments || 0),
            shares: acc.shares + (p.shares || 0),
            saves: acc.saves + (p.saves || 0),
        }),
        { views: 0, likes: 0, comments: 0, shares: 0, saves: 0 },
    );
}

export function accountEngagementRate(report: AccountReport): number {
    const posts = postsOf(report);
    if (posts.length === 0) return 0;
    if (report.platform === 'instagram' || report.platform === 'facebook') {
        return posts.reduce((sum, p) => sum + (p.engagement_rate || 0), 0) / posts.length;
    }
    const totals = summarise(posts);
    const engagements = totals.likes + totals.comments + totals.shares + totals.saves;
    if (totals.views <= 0) return 0;
    return engagements / totals.views;
}

export type ChartMetric = 'views' | 'likes' | 'engagement_rate' | 'posts';

function bucketEngagementRate(bucket: PostMetrics[], platform: string): number {
    if (bucket.length === 0) return 0;
    if (platform === 'instagram' || platform === 'facebook') {
        const sum = bucket.reduce((s, p) => s + (p.engagement_rate || 0), 0);
        return sum / bucket.length;
    }
    const totals = summarise(bucket);
    const engagements = totals.likes + totals.comments + totals.shares + totals.saves;
    if (totals.views <= 0) return 0;
    return engagements / totals.views;
}

export function chartGrain(posts: PostMetrics[], allTime: boolean): 'day' | 'week' {
    if (!allTime) return 'day';
    const dates = posts.map(postMoment).filter((d): d is dayjs.Dayjs => !!d);
    if (dates.length < 2) return 'day';
    const min = dates.reduce((a, b) => (a.isBefore(b) ? a : b));
    const max = dates.reduce((a, b) => (a.isAfter(b) ? a : b));
    return max.diff(min, 'day') > 90 ? 'week' : 'day';
}

export function chartSeries(posts: PostMetrics[], metric: ChartMetric, grain: 'day' | 'week', platform: string) {
    const bucketPosts = new Map<string, PostMetrics[]>();
    for (const post of posts) {
        const at = postMoment(post);
        if (!at) continue;
        const key = grain === 'week' ? at.startOf('isoWeek').format('YYYY-MM-DD') : at.format('YYYY-MM-DD');
        const list = bucketPosts.get(key) ?? [];
        list.push(post);
        bucketPosts.set(key, list);
    }
    return Array.from(bucketPosts.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, bucket]) => {
            let value = 0;
            if (metric === 'views') value = summarise(bucket).views;
            else if (metric === 'likes') value = summarise(bucket).likes;
            else if (metric === 'engagement_rate') value = bucketEngagementRate(bucket, platform);
            else value = bucket.length;
            return { date, value };
        });
}

export function engagementRateChartHelp(platform: string): string {
    if (platform === 'instagram' || platform === 'facebook') {
        const base = 'Each post: (likes + comments + shares + saves)';
        const denom = platform === 'facebook'
            ? ' ÷ views when available, otherwise ÷ followers (page follower count at fetch time).'
            : ' ÷ followers (profile follower count at fetch time).';
        return base + denom + ' Each day or week: average ER of posts published in that period.';
    }
    return 'Each day or week: (likes + comments + shares + saves) ÷ views, summed across posts published in that period. '
        + 'Posts with no views count as 0 toward the rate.';
}

export type ContentBucket = { type: string; count: number; views: number };

export function contentBuckets(posts: PostMetrics[]): ContentBucket[] {
    const map = new Map<string, ContentBucket>();
    for (const post of posts) {
        const type = post.post_type || 'post';
        const row = map.get(type) ?? { type, count: 0, views: 0 };
        row.count += 1;
        row.views += post.views || 0;
        map.set(type, row);
    }
    return Array.from(map.values()).sort((a, b) => b.views - a.views || b.count - a.count);
}
